import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { environment } from 'src/environments/environment';
import { CookieConsentService } from '../../../services/cookie-consent.service';
import { CookieSettingsComponent } from '../cookie-settings/cookie-settings.component';

/**
 * Cookie consent banner component following GDPR best practices.
 *
 * Features:
 * - Non-intrusive bottom banner design
 * - Clear Accept All / Decline All / Customize options
 * - Keyboard accessible
 * - Mobile responsive (optimized for mobile-first design)
 * - Auto-hides after user choice
 *
 * The banner appears on first visit and remains until user makes a choice.
 */
@Component({
  selector: 'cookie-banner',
  standalone: true,
  imports: [TranslateModule, NzButtonModule, NzIconModule, CookieSettingsComponent],
  templateUrl: './cookie-banner.component.html',
  styleUrls: ['./cookie-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookieBannerComponent {
  readonly #cookieConsentService = inject(CookieConsentService);

  /** Control banner visibility - computed from service */
  protected readonly visible = this.#cookieConsentService.shouldShowBanner;

  /** Loading state for async actions */
  protected readonly loading = signal(false);

  /** Show detailed settings modal */
  protected readonly showSettings = signal(false);

  protected readonly termsAndConditionsLink = `${environment.LANDING_PAGE_URL}/terms?type=cookie_policy`;

  /**
   * Accept all cookies
   */
  protected async onAcceptAll(): Promise<void> {
    this.loading.set(true);
    try {
      await this.#cookieConsentService.acceptAll();
      // Banner will automatically hide via computed signal
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Decline all optional cookies
   */
  protected async onDeclineAll(): Promise<void> {
    this.loading.set(true);
    try {
      await this.#cookieConsentService.declineAll();
      // Banner will automatically hide via computed signal
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Open cookie settings modal
   */
  protected onCustomize(): void {
    this.showSettings.set(true);
  }

  /**
   * Handle settings modal close
   */
  protected onSettingsClosed(): void {
    this.showSettings.set(false);
    // Banner will automatically hide if user has made a choice (via computed signal)
  }
}
